import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Reports.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '/api';

// Icons
const ChartIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M18 20V10M12 20V4M6 20v-6"/>
  </svg>
);

const DollarIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/>
  </svg>
);

const OrdersIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
    <polyline points="14,2 14,8 20,8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
  </svg>
);

const ProductIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/>
    <polyline points="3.27,6.96 12,12.01 20.73,6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/>
  </svg>
);

const StoreIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
    <polyline points="9,22 9,12 15,12 15,22"/>
  </svg>
);

const FundraiserIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/>
    <path d="M20 12h2M2 12h2"/>
  </svg>
);

const DownloadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7,10 12,15 17,10"/><line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);

const BackIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="15,18 9,12 15,6"/>
  </svg>
);

const RefreshIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="23,4 23,10 17,10"/><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10"/>
  </svg>
);

const Reports = () => {
  const navigate = useNavigate();

  // State
  const [activeTab, setActiveTab] = useState('summary');
  const [period, setPeriod] = useState('all_time');
  const [storeFilter, setStoreFilter] = useState('');
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);

  // Report data
  const [summaryData, setSummaryData] = useState(null);
  const [salesData, setSalesData] = useState(null);
  const [ordersData, setOrdersData] = useState(null);
  const [productsData, setProductsData] = useState(null);
  const [storesData, setStoresData] = useState(null);
  const [fundraiserData, setFundraiserData] = useState(null);

  // Sales grouping
  const [salesGroupBy, setSalesGroupBy] = useState('store');

  // Fetch stores for filter dropdown
  useEffect(() => {
    fetchStores();
  }, []);

  // Fetch data when tab or filters change
  useEffect(() => {
    fetchReportData();
  }, [activeTab, period, storeFilter, salesGroupBy]);

  const fetchStores = async () => {
    try {
      const response = await fetch(`${API_BASE}/team-stores/admin`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setStores(data.stores || []);
      }
    } catch (error) {
      console.error('Error fetching stores:', error);
    }
  };

  const fetchReportData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ period });
      if (storeFilter) params.append('store_id', storeFilter);

      switch (activeTab) {
        case 'summary':
          const summaryRes = await fetch(`${API_BASE}/team-stores/reports/summary?${params}`, {
            credentials: 'include'
          });
          if (summaryRes.ok) setSummaryData(await summaryRes.json());
          break;

        case 'sales':
          params.append('group_by', salesGroupBy);
          const salesRes = await fetch(`${API_BASE}/team-stores/reports/sales?${params}`, {
            credentials: 'include'
          });
          if (salesRes.ok) setSalesData(await salesRes.json());
          break;

        case 'orders':
          const ordersRes = await fetch(`${API_BASE}/team-stores/reports/orders?${params}`, {
            credentials: 'include'
          });
          if (ordersRes.ok) setOrdersData(await ordersRes.json());
          break;

        case 'products':
          const productsRes = await fetch(`${API_BASE}/team-stores/reports/products?${params}`, {
            credentials: 'include'
          });
          if (productsRes.ok) setProductsData(await productsRes.json());
          break;

        case 'stores':
          const storesRes = await fetch(`${API_BASE}/team-stores/reports/stores?${params}`, {
            credentials: 'include'
          });
          if (storesRes.ok) setStoresData(await storesRes.json());
          break;

        case 'fundraiser':
          const fundraiserUrl = storeFilter
            ? `${API_BASE}/team-stores/reports/fundraiser/${storeFilter}?${params}`
            : `${API_BASE}/team-stores/reports/fundraiser?${params}`;
          const fundraiserRes = await fetch(fundraiserUrl, {
            credentials: 'include'
          });
          if (fundraiserRes.ok) setFundraiserData(await fundraiserRes.json());
          break;
      }
    } catch (error) {
      console.error('Error fetching report data:', error);
      showNotification('Error loading report data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async (type) => {
    try {
      const params = new URLSearchParams({ period });
      if (storeFilter) params.append('store_id', storeFilter);

      const response = await fetch(`${API_BASE}/team-stores/reports/export/${type}?${params}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `team-store-${type}-${period}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        showNotification('CSV exported successfully!', 'success');
      } else {
        showNotification('Error exporting CSV', 'error');
      }
    } catch (error) {
      console.error('Error exporting CSV:', error);
      showNotification('Error exporting CSV', 'error');
    }
  };

  const handleExportPDF = async (type) => {
    try {
      const params = new URLSearchParams({ period });
      if (storeFilter) params.append('store_id', storeFilter);

      const response = await fetch(`${API_BASE}/team-stores/reports/pdf/${type}?${params}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `team-store-${type}-report.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        showNotification('PDF exported successfully!', 'success');
      } else {
        showNotification('Error exporting PDF', 'error');
      }
    } catch (error) {
      console.error('Error exporting PDF:', error);
      showNotification('Error exporting PDF', 'error');
    }
  };

  const showNotification = (message, type) => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const periodOptions = [
    { value: 'today', label: 'Today' },
    { value: 'this_week', label: 'This Week' },
    { value: 'this_month', label: 'This Month' },
    { value: 'this_quarter', label: 'This Quarter' },
    { value: 'this_year', label: 'This Year' },
    { value: 'all_time', label: 'All Time' }
  ];

  const tabs = [
    { id: 'summary', label: 'Summary', icon: <ChartIcon /> },
    { id: 'sales', label: 'Sales & Revenue', icon: <DollarIcon /> },
    { id: 'orders', label: 'Order Fulfillment', icon: <OrdersIcon /> },
    { id: 'products', label: 'Product Performance', icon: <ProductIcon /> },
    { id: 'stores', label: 'Store Analytics', icon: <StoreIcon /> },
    { id: 'fundraiser', label: 'Fundraiser', icon: <FundraiserIcon /> }
  ];

  // Summary Tab Content
  const renderSummary = () => {
    if (!summaryData) return <div className="tsr-loading">Loading...</div>;

    const revenue = summaryData.revenue || {};
    const stores = summaryData.stores || {};
    const orderStatus = summaryData.orderStatus || [];
    const trends = summaryData.trends || [];

    return (
      <div className="tsr-summary">
        {/* Key Metrics Cards */}
        <div className="tsr-metrics-grid">
          <div className="tsr-metric-card tsr-metric-revenue">
            <div className="tsr-metric-icon"><DollarIcon /></div>
            <div className="tsr-metric-content">
              <span className="tsr-metric-label">Total Revenue</span>
              <span className="tsr-metric-value">{formatCurrency(revenue.total || 0)}</span>
            </div>
          </div>
          <div className="tsr-metric-card tsr-metric-orders">
            <div className="tsr-metric-icon"><OrdersIcon /></div>
            <div className="tsr-metric-content">
              <span className="tsr-metric-label">Total Orders</span>
              <span className="tsr-metric-value">{revenue.orders || 0}</span>
            </div>
          </div>
          <div className="tsr-metric-card tsr-metric-customers">
            <div className="tsr-metric-icon"><ChartIcon /></div>
            <div className="tsr-metric-content">
              <span className="tsr-metric-label">Unique Customers</span>
              <span className="tsr-metric-value">{revenue.uniqueCustomers || 0}</span>
            </div>
          </div>
          <div className="tsr-metric-card tsr-metric-avg">
            <div className="tsr-metric-icon"><DollarIcon /></div>
            <div className="tsr-metric-content">
              <span className="tsr-metric-label">Avg Order Value</span>
              <span className="tsr-metric-value">{formatCurrency(revenue.avgOrderValue || 0)}</span>
            </div>
          </div>
        </div>

        {/* Store Status & Order Status */}
        <div className="tsr-summary-row">
          <div className="tsr-summary-card">
            <h3>Store Status</h3>
            <div className="tsr-status-grid">
              <div className="tsr-status-item">
                <span className="tsr-status-count tsr-status-active">{stores.active || 0}</span>
                <span className="tsr-status-label">Active</span>
              </div>
              <div className="tsr-status-item">
                <span className="tsr-status-count tsr-status-draft">{stores.draft || 0}</span>
                <span className="tsr-status-label">Draft</span>
              </div>
              <div className="tsr-status-item">
                <span className="tsr-status-count tsr-status-closed">{stores.closed || 0}</span>
                <span className="tsr-status-label">Closed</span>
              </div>
              <div className="tsr-status-item">
                <span className="tsr-status-count">{stores.total || 0}</span>
                <span className="tsr-status-label">Total</span>
              </div>
            </div>
          </div>

          <div className="tsr-summary-card">
            <h3>Order Status</h3>
            {orderStatus.length > 0 ? (
              <div className="tsr-order-status-list">
                {orderStatus.map((status, idx) => (
                  <div key={idx} className="tsr-order-status-row">
                    <span className={`tsr-order-status-badge tsr-status-${status.status}`}>
                      {status.status}
                    </span>
                    <span className="tsr-order-status-count">{status.count}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="tsr-no-data">No orders yet</p>
            )}
          </div>
        </div>

        {/* Trends Chart (Simple Bar) */}
        {trends.length > 0 && (
          <div className="tsr-summary-card tsr-trends-card">
            <h3>Last 7 Days</h3>
            <div className="tsr-simple-chart">
              {trends.map((day, idx) => {
                const maxRevenue = Math.max(...trends.map(d => d.revenue || 0), 1);
                return (
                  <div key={idx} className="tsr-chart-bar-container">
                    <div
                      className="tsr-chart-bar"
                      style={{
                        height: `${Math.max(10, ((day.revenue || 0) / maxRevenue) * 100)}%`
                      }}
                      title={`${formatDate(day.date)}: ${formatCurrency(day.revenue || 0)} (${day.orders || 0} orders)`}
                    >
                      <span className="tsr-chart-tooltip">
                        {formatCurrency(day.revenue || 0)}<br/>
                        {day.orders || 0} orders
                      </span>
                    </div>
                    <span className="tsr-chart-label">
                      {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' })}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Export Buttons */}
        <div className="tsr-export-section">
          <button className="tsr-btn tsr-btn-export" onClick={() => handleExportPDF('summary')}>
            <DownloadIcon /> Export PDF
          </button>
        </div>
      </div>
    );
  };

  // Sales Tab Content
  const renderSales = () => {
    if (!salesData) return <div className="tsr-loading">Loading...</div>;

    const totals = salesData.totals || {};
    const data = salesData.data || [];

    return (
      <div className="tsr-sales">
        {/* Group By Selector */}
        <div className="tsr-group-selector">
          <span>Group by:</span>
          <select value={salesGroupBy} onChange={(e) => setSalesGroupBy(e.target.value)}>
            <option value="store">Store</option>
            <option value="day">Day</option>
            <option value="week">Week</option>
            <option value="month">Month</option>
          </select>
        </div>

        {/* Totals */}
        <div className="tsr-totals-bar">
          <div className="tsr-total-item">
            <span className="tsr-total-label">Total Revenue</span>
            <span className="tsr-total-value">{formatCurrency(totals.revenue || 0)}</span>
          </div>
          <div className="tsr-total-item">
            <span className="tsr-total-label">Total Orders</span>
            <span className="tsr-total-value">{totals.orders || 0}</span>
          </div>
          <div className="tsr-total-item">
            <span className="tsr-total-label">Unique Customers</span>
            <span className="tsr-total-value">{totals.customers || 0}</span>
          </div>
        </div>

        {/* Sales Table */}
        <div className="tsr-table-container">
          {data.length > 0 ? (
            <table className="tsr-table">
              <thead>
                <tr>
                  {salesGroupBy === 'store' ? (
                    <>
                      <th>Store</th>
                      <th>Status</th>
                    </>
                  ) : (
                    <th>{salesGroupBy === 'day' ? 'Date' : salesGroupBy === 'week' ? 'Week Starting' : 'Month'}</th>
                  )}
                  <th>Orders</th>
                  <th>Revenue</th>
                  <th>Customers</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row, idx) => (
                  <tr key={idx}>
                    {salesGroupBy === 'store' ? (
                      <>
                        <td>{row.store_name}</td>
                        <td>
                          <span className={`tsr-status-badge tsr-status-${row.store_status}`}>
                            {row.store_status}
                          </span>
                        </td>
                      </>
                    ) : (
                      <td>{formatDate(row.date || row.week_start || row.month_start)}</td>
                    )}
                    <td>{row.order_count || 0}</td>
                    <td>{formatCurrency(row.total_revenue || 0)}</td>
                    <td>{row.unique_customers || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="tsr-no-data">No sales data available for the selected period.</p>
          )}
        </div>

        {/* Export Buttons */}
        <div className="tsr-export-section">
          <button className="tsr-btn tsr-btn-export" onClick={() => handleExportCSV('sales')}>
            <DownloadIcon /> Export CSV
          </button>
          <button className="tsr-btn tsr-btn-export" onClick={() => handleExportPDF('sales')}>
            <DownloadIcon /> Export PDF
          </button>
        </div>
      </div>
    );
  };

  // Orders Tab Content
  const renderOrders = () => {
    if (!ordersData) return <div className="tsr-loading">Loading...</div>;

    const statusSummary = ordersData.statusSummary || [];
    const itemBreakdown = ordersData.itemSummary?.breakdown || [];
    const totalItems = ordersData.itemSummary?.totalItems || 0;
    const orders = ordersData.orders || [];

    return (
      <div className="tsr-orders">
        {/* Status Summary */}
        <div className="tsr-order-summary-cards">
          {statusSummary.length > 0 ? (
            statusSummary.map((status, idx) => (
              <div key={idx} className={`tsr-order-summary-card tsr-status-${status.status}`}>
                <span className="tsr-summary-count">{status.count}</span>
                <span className="tsr-summary-label">{status.status}</span>
                <span className="tsr-summary-revenue">{formatCurrency(status.revenue)}</span>
              </div>
            ))
          ) : (
            <div className="tsr-no-data">No order status data available</div>
          )}
        </div>

        {/* Item Breakdown */}
        {itemBreakdown.length > 0 && (
          <div className="tsr-items-breakdown">
            <h3>Top Items Ordered ({totalItems} total items)</h3>
            <div className="tsr-items-list">
              {itemBreakdown.slice(0, 10).map((item, idx) => (
                <div key={idx} className="tsr-item-row">
                  <span className="tsr-item-rank">#{idx + 1}</span>
                  <span className="tsr-item-name">{item.name} ({formatCurrency(item.price)})</span>
                  <span className="tsr-item-qty">{item.count} sold</span>
                  <span className="tsr-item-revenue">{formatCurrency(item.revenue)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Orders Table */}
        <div className="tsr-table-container">
          <h3>Recent Orders</h3>
          {orders.length > 0 ? (
            <table className="tsr-table tsr-orders-table">
              <thead>
                <tr>
                  <th>Order #</th>
                  <th>Store</th>
                  <th>Date</th>
                  <th>Customer</th>
                  <th>Items</th>
                  <th>Total</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {orders.slice(0, 100).map((order, idx) => (
                  <tr key={idx}>
                    <td className="tsr-order-number">#{order.wc_order_number}</td>
                    <td>{order.store_name}</td>
                    <td>{formatDateTime(order.order_date)}</td>
                    <td>
                      <div className="tsr-customer-cell">
                        <span className="tsr-customer-name">{order.customer_name}</span>
                        <span className="tsr-customer-email">{order.customer_email}</span>
                      </div>
                    </td>
                    <td>{order.item_count}</td>
                    <td>{formatCurrency(order.order_total)}</td>
                    <td>
                      <span className={`tsr-status-badge tsr-status-${order.order_status}`}>
                        {order.order_status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="tsr-no-data">No orders found for the selected period.</p>
          )}
        </div>

        {/* Export Buttons */}
        <div className="tsr-export-section">
          <button className="tsr-btn tsr-btn-export" onClick={() => handleExportCSV('orders')}>
            <DownloadIcon /> Export CSV
          </button>
          <button className="tsr-btn tsr-btn-export" onClick={() => handleExportPDF('orders')}>
            <DownloadIcon /> Export PDF
          </button>
        </div>
      </div>
    );
  };

  // Products Tab Content
  const renderProducts = () => {
    if (!productsData) return <div className="tsr-loading">Loading...</div>;

    const totals = productsData.totals || {};
    const pricing = productsData.pricing || {};
    const topSellers = productsData.topSellers || [];
    const categories = productsData.categories || [];

    return (
      <div className="tsr-products">
        {/* Totals */}
        <div className="tsr-totals-bar">
          <div className="tsr-total-item">
            <span className="tsr-total-label">Products Sold</span>
            <span className="tsr-total-value">{totals.products || 0}</span>
          </div>
          <div className="tsr-total-item">
            <span className="tsr-total-label">Total Qty Sold</span>
            <span className="tsr-total-value">{totals.totalQtySold || 0}</span>
          </div>
          <div className="tsr-total-item">
            <span className="tsr-total-label">Total Revenue</span>
            <span className="tsr-total-value">{formatCurrency(totals.totalRevenue || 0)}</span>
          </div>
        </div>

        {/* Pricing Stats */}
        <div className="tsr-pricing-stats">
          <h3>Pricing Overview</h3>
          <div className="tsr-pricing-grid">
            <div className="tsr-pricing-item">
              <span className="tsr-pricing-label">Min Price</span>
              <span className="tsr-pricing-value">{formatCurrency(pricing.min || 0)}</span>
            </div>
            <div className="tsr-pricing-item">
              <span className="tsr-pricing-label">Max Price</span>
              <span className="tsr-pricing-value">{formatCurrency(pricing.max || 0)}</span>
            </div>
            <div className="tsr-pricing-item">
              <span className="tsr-pricing-label">Avg Price</span>
              <span className="tsr-pricing-value">{formatCurrency(pricing.avg || 0)}</span>
            </div>
          </div>
        </div>

        {/* Top Sellers */}
        {topSellers.length > 0 && (
          <div className="tsr-top-sellers">
            <h3>Top Sellers</h3>
            <div className="tsr-top-sellers-list">
              {topSellers.map((product, idx) => (
                <div key={idx} className="tsr-top-seller-item">
                  <span className="tsr-seller-rank">#{idx + 1}</span>
                  <div className="tsr-seller-info">
                    <span className="tsr-seller-name">{product.name}</span>
                    <span className="tsr-seller-store">{product.store_name}</span>
                  </div>
                  <div className="tsr-seller-stats">
                    <span className="tsr-seller-qty">{product.total_qty || 0} sold</span>
                    <span className="tsr-seller-revenue">{formatCurrency(product.total_revenue || 0)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Categories */}
        {categories.length > 0 && (
          <div className="tsr-categories">
            <h3>Categories</h3>
            <div className="tsr-table-container">
              <table className="tsr-table">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Store</th>
                    <th>Products</th>
                    <th>Type</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.map((cat, idx) => (
                    <tr key={idx}>
                      <td>{cat.category_name}</td>
                      <td>{cat.store_name}</td>
                      <td>{cat.product_count || 0}</td>
                      <td>{cat.is_package ? 'Package' : 'Category'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Export Buttons */}
        <div className="tsr-export-section">
          <button className="tsr-btn tsr-btn-export" onClick={() => handleExportCSV('products')}>
            <DownloadIcon /> Export CSV
          </button>
        </div>
      </div>
    );
  };

  // Stores Tab Content
  const renderStores = () => {
    if (!storesData) return <div className="tsr-loading">Loading...</div>;

    const lifecycle = storesData.lifecycle || [];
    const deadlines = storesData.deadlines || [];
    const storesList = storesData.stores || [];

    return (
      <div className="tsr-stores-analytics">
        {/* Lifecycle Stats */}
        <div className="tsr-lifecycle-stats">
          <h3>Store Lifecycle</h3>
          <div className="tsr-lifecycle-grid">
            {lifecycle.length > 0 ? (
              lifecycle.map((item, idx) => (
                <div key={idx} className={`tsr-lifecycle-item tsr-status-${item.status}`}>
                  <span className="tsr-lifecycle-count">{item.count}</span>
                  <span className="tsr-lifecycle-label">{item.status}</span>
                </div>
              ))
            ) : (
              <p className="tsr-no-data">No lifecycle data available</p>
            )}
          </div>
        </div>

        {/* Deadline Analysis */}
        {deadlines.length > 0 && (
          <div className="tsr-deadlines">
            <h3>Active Store Deadlines</h3>
            <div className="tsr-deadline-grid">
              {deadlines.map((d, idx) => (
                <div key={idx} className="tsr-deadline-item">
                  <span className="tsr-deadline-count">{d.count}</span>
                  <span className="tsr-deadline-label">{d.status}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Store Performance Table */}
        <div className="tsr-table-container">
          <h3>Store Performance</h3>
          {storesList.length > 0 ? (
            <table className="tsr-table tsr-stores-table">
              <thead>
                <tr>
                  <th>Store</th>
                  <th>Status</th>
                  <th>Created By</th>
                  <th>Categories</th>
                  <th>Products</th>
                  <th>Orders</th>
                  <th>Revenue</th>
                  <th>Customers</th>
                </tr>
              </thead>
              <tbody>
                {storesList.map((store, idx) => (
                  <tr key={idx}>
                    <td>
                      <div className="tsr-store-cell">
                        <span className="tsr-store-name">{store.name}</span>
                        {store.deadline && (
                          <span className="tsr-store-deadline">
                            Deadline: {formatDate(store.deadline)}
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className={`tsr-status-badge tsr-status-${store.status}`}>
                        {store.status}
                      </span>
                    </td>
                    <td>{store.created_by || 'Unknown'}</td>
                    <td>{store.category_count || 0}</td>
                    <td>{store.product_count || 0}</td>
                    <td>{store.order_count || 0}</td>
                    <td>{formatCurrency(store.total_revenue || 0)}</td>
                    <td>{store.unique_customers || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="tsr-no-data">No store data available</p>
          )}
        </div>

        {/* Export Buttons */}
        <div className="tsr-export-section">
          <button className="tsr-btn tsr-btn-export" onClick={() => handleExportCSV('stores')}>
            <DownloadIcon /> Export CSV
          </button>
        </div>
      </div>
    );
  };

  // Fundraiser Tab Content
  const renderFundraiser = () => {
    if (!fundraiserData) return <div className="tsr-loading">Loading...</div>;

    const formatCurrency = (val) => `$${parseFloat(val || 0).toFixed(2)}`;
    const formatFundraiserRate = (type, percentage, flatAmount) =>
      type === 'flat' ? `$${parseFloat(flatAmount || 0).toFixed(2)} flat` : `${percentage || 0}% markup`;

    // If viewing a specific store (detailed view)
    if (storeFilter && fundraiserData.totals) {
      const { totals, byOrder, byProduct } = fundraiserData;

      return (
        <div className="tsr-fundraiser">
          {/* Store Summary */}
          <div className="tsr-section">
            <h2>{totals.store_name} - Fundraiser Report</h2>
            <div className="tsr-fundraiser-status">
              <span className={`tsr-status-badge ${totals.fundraiser_enabled ? 'active' : 'inactive'}`}>
                {totals.fundraiser_enabled ? 'Fundraiser Enabled' : 'Fundraiser Disabled'}
              </span>
              {totals.fundraiser_enabled && (
                <span className="tsr-percentage-badge">{formatFundraiserRate(totals.fundraiser_type, totals.fundraiser_percentage, totals.fundraiser_flat_amount)}</span>
              )}
            </div>
          </div>

          {/* Metrics */}
          <div className="tsr-metrics-grid tsr-metrics-3">
            <div className="tsr-metric-card tsr-metric-revenue">
              <div className="tsr-metric-content">
                <span className="tsr-metric-label">Total Revenue</span>
                <span className="tsr-metric-value">{formatCurrency(totals.total_revenue)}</span>
              </div>
            </div>
            <div className="tsr-metric-card tsr-metric-fundraiser">
              <div className="tsr-metric-content">
                <span className="tsr-metric-label">Fundraiser Total</span>
                <span className="tsr-metric-value tsr-fundraiser-value">{formatCurrency(totals.total_fundraiser)}</span>
              </div>
            </div>
            <div className="tsr-metric-card">
              <div className="tsr-metric-content">
                <span className="tsr-metric-label">Orders with Fundraiser</span>
                <span className="tsr-metric-value">{totals.orders_with_fundraiser || 0}</span>
              </div>
            </div>
          </div>

          {/* By Product */}
          {byProduct && byProduct.length > 0 && (
            <div className="tsr-section">
              <h3>Fundraiser by Product</h3>
              <table className="tsr-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Qty Sold</th>
                    <th>Revenue</th>
                    <th>Fundraiser Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {byProduct.map((prod, idx) => (
                    <tr key={idx}>
                      <td>{prod.product_name}</td>
                      <td>{prod.total_qty}</td>
                      <td>{formatCurrency(prod.total_revenue)}</td>
                      <td className="tsr-fundraiser-amount">{formatCurrency(prod.total_fundraiser)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* By Order */}
          {byOrder && byOrder.length > 0 && (
            <div className="tsr-section">
              <h3>Fundraiser by Order</h3>
              <table className="tsr-table">
                <thead>
                  <tr>
                    <th>Order #</th>
                    <th>Date</th>
                    <th>Customer</th>
                    <th>Order Total</th>
                    <th>Fundraiser Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {byOrder.slice(0, 50).map((order) => (
                    <tr key={order.id}>
                      <td>{order.wc_order_number}</td>
                      <td>{new Date(order.order_date).toLocaleDateString()}</td>
                      <td>{order.customer_name}</td>
                      <td>{formatCurrency(order.order_total)}</td>
                      <td className="tsr-fundraiser-amount">{formatCurrency(order.fundraiser_total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Export */}
          <div className="tsr-export-section">
            <button className="tsr-btn tsr-btn-export" onClick={() => handleExportCSV('fundraiser')}>
              <DownloadIcon /> Export CSV
            </button>
            <button className="tsr-btn tsr-btn-export" onClick={() => handleExportPDF('fundraiser')}>
              <DownloadIcon /> Export PDF
            </button>
          </div>
        </div>
      );
    }

    // Summary view (all stores)
    const { summary } = fundraiserData;
    const totalFundraiser = (summary || []).reduce((sum, s) => sum + parseFloat(s.total_fundraiser || 0), 0);
    const totalRevenue = (summary || []).reduce((sum, s) => sum + parseFloat(s.total_revenue || 0), 0);
    const storesWithFundraiser = (summary || []).filter(s => s.fundraiser_enabled).length;

    return (
      <div className="tsr-fundraiser">
        {/* Summary Metrics */}
        <div className="tsr-metrics-grid tsr-metrics-3">
          <div className="tsr-metric-card tsr-metric-fundraiser">
            <div className="tsr-metric-content">
              <span className="tsr-metric-label">Total Fundraiser Amount</span>
              <span className="tsr-metric-value tsr-fundraiser-value">{formatCurrency(totalFundraiser)}</span>
            </div>
          </div>
          <div className="tsr-metric-card tsr-metric-revenue">
            <div className="tsr-metric-content">
              <span className="tsr-metric-label">Total Revenue</span>
              <span className="tsr-metric-value">{formatCurrency(totalRevenue)}</span>
            </div>
          </div>
          <div className="tsr-metric-card">
            <div className="tsr-metric-content">
              <span className="tsr-metric-label">Stores with Fundraiser</span>
              <span className="tsr-metric-value">{storesWithFundraiser}</span>
            </div>
          </div>
        </div>

        {/* Stores Table */}
        <div className="tsr-section">
          <h3>Fundraiser by Store</h3>
          <p className="tsr-hint">Select a store from the filter above to see detailed breakdown.</p>
          {summary && summary.length > 0 ? (
            <table className="tsr-table">
              <thead>
                <tr>
                  <th>Store</th>
                  <th>Enabled</th>
                  <th>Rate</th>
                  <th>Orders</th>
                  <th>Revenue</th>
                  <th>Fundraiser Amount</th>
                </tr>
              </thead>
              <tbody>
                {summary.map((store) => (
                  <tr key={store.store_id} className={store.fundraiser_enabled ? '' : 'tsr-row-muted'}>
                    <td>{store.store_name}</td>
                    <td>
                      <span className={`tsr-status-dot ${store.fundraiser_enabled ? 'active' : ''}`}></span>
                      {store.fundraiser_enabled ? 'Yes' : 'No'}
                    </td>
                    <td>{formatFundraiserRate(store.fundraiser_type, store.fundraiser_percentage, store.fundraiser_flat_amount)}</td>
                    <td>{store.total_orders || 0}</td>
                    <td>{formatCurrency(store.total_revenue)}</td>
                    <td className="tsr-fundraiser-amount">{formatCurrency(store.total_fundraiser)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="tsr-no-data">No fundraiser data available</p>
          )}
        </div>

        {/* Export */}
        <div className="tsr-export-section">
          <button className="tsr-btn tsr-btn-export" onClick={() => handleExportCSV('fundraiser')}>
            <DownloadIcon /> Export CSV
          </button>
          <button className="tsr-btn tsr-btn-export" onClick={() => handleExportPDF('fundraiser')}>
            <DownloadIcon /> Export PDF
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="tsr-container">
      {/* Notification */}
      {notification && (
        <div className={`tsr-notification tsr-notification-${notification.type}`}>
          {notification.message}
        </div>
      )}

      {/* Header */}
      <div className="tsr-header">
        <div className="tsr-header-left">
          <button className="tsr-back-btn" onClick={() => navigate('/team-store-management')}>
            <BackIcon /> Back to Dashboard
          </button>
          <h1><ChartIcon /> Reports</h1>
        </div>
        <div className="tsr-header-right">
          <button className="tsr-btn tsr-btn-refresh" onClick={fetchReportData} disabled={loading}>
            <RefreshIcon /> Refresh
          </button>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="tsr-filters-bar">
        <div className="tsr-filter-group">
          <label>Time Period:</label>
          <select value={period} onChange={(e) => setPeriod(e.target.value)}>
            {periodOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div className="tsr-filter-group">
          <label>Store:</label>
          <select value={storeFilter} onChange={(e) => setStoreFilter(e.target.value)}>
            <option value="">All Stores</option>
            {stores.map(store => (
              <option key={store.id} value={store.id}>{store.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Tabs */}
      <div className="tsr-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`tsr-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.icon}
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="tsr-content">
        {loading ? (
          <div className="tsr-loading">
            <div className="tsr-spinner"></div>
            <p>Loading report data...</p>
          </div>
        ) : (
          <>
            {activeTab === 'summary' && renderSummary()}
            {activeTab === 'sales' && renderSales()}
            {activeTab === 'orders' && renderOrders()}
            {activeTab === 'products' && renderProducts()}
            {activeTab === 'stores' && renderStores()}
            {activeTab === 'fundraiser' && renderFundraiser()}
          </>
        )}
      </div>
    </div>
  );
};

export default Reports;
